import { createPortal } from "react-dom"
import styled from "styled-components"
import { Button, FormField, Modal, useForm } from "@prosolve/common-components-ui"
import { FaSave, FaTimes } from "react-icons/fa"
import useSnackbar from "../../hooks/useSnackbar"
import {
  DEFAULT_JOB,
  dayOfWeekOptions,
  getJobId,
  intervalUnitOptions,
  methodOptions,
  monthOptions,
  normalizeTimeOfDay,
  recurrenceOptions,
  scheduleTypeOptions,
  toFormJob
} from "./jobUtils"

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 760px;
  max-width: calc(100vw - 80px);
  padding-bottom: 10px;
`

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`

const RowGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 10px;
`

const HiddenSubmit = styled.button`
  display: none;
`

const numberOptions = (start, end) => {
  const options = []
  for (let i = start; i <= end; i++) {
    options.push({ value: i, text: `${i}` })
  }
  return options
}

const getInitData = (job) => job ? toFormJob(job) : DEFAULT_JOB

export default ({
  job,
  title,
  isNameDisabled,
  intervalUnitOptions: intervalUnitOptionsOverride,
  onClose,
  onSubmit
}) => {
  const { newMessage } = useSnackbar()
  const formData = useForm({
    requiredFields: ['name', 'endpointUrl'],
    optionalFields: [
      'description',
      'enabled',
      'method',
      'headersText',
      'body',
      'scheduleType',
      'recurrence',
      'timeOfDay',
      'dayOfWeek',
      'dayOfMonth',
      'month',
      'intervalValue',
      'intervalUnit'
    ],
    initData: getInitData(job)
  })

  const { values } = formData
  const jobId = getJobId(job)
  const unitOptions = intervalUnitOptionsOverride || intervalUnitOptions

  const submit = async () => {
    if (!formData.isValid()) {
      return
    }

    let headers;
    try {
      headers = JSON.parse(values.headersText || '{}')
    } catch (e) {
      newMessage({ message: 'Headers must be valid JSON', severity: 'failure' })
      return
    }

    const savedJob = await onSubmit({
      ...values,
      headers,
      enabled: Boolean(values.enabled),
      timeOfDay: normalizeTimeOfDay(values.timeOfDay),
      intervalValue: Number(values.intervalValue),
      dayOfWeek: Number(values.dayOfWeek),
      dayOfMonth: Number(values.dayOfMonth),
      month: Number(values.month)
    })

    if (savedJob) {
      onClose()
    }
  }

  const footer = (
    <ButtonsContainer>
      <Button onClick={onClose}>
        <RowGroup>
          <FaTimes />
          Cancel
        </RowGroup>
      </Button>
      <Button $colored={true} onClick={submit}>
        <RowGroup>
          <FaSave />
          Save
        </RowGroup>
      </Button>
    </ButtonsContainer>
  )

  return createPortal(
    <Modal
      title={title || (jobId ? 'Edit Job' : 'New Job')}
      isAutoHeight={true}
      isAutoWidth={true}
      onClose={onClose}
      footer={footer}
    >
      <form onSubmit={(e) => { e.preventDefault(); submit() }}>
        <ModalContainer>
          <FieldGrid>
            <FormField
              label='Name*'
              fieldId='name'
              type='text'
              width='100%'
              disabled={isNameDisabled}
              formData={formData}
            />
            <FormField
              label='Method'
              fieldId='method'
              type='select'
              width='100%'
              options={methodOptions}
              formData={formData}
            />
          </FieldGrid>

          <FormField
            label='Description'
            fieldId='description'
            type='text'
            width='100%'
            formData={formData}
          />

          <FormField
            label='Endpoint URL*'
            fieldId='endpointUrl'
            type='text'
            width='100%'
            placeholder='https://example.com/api/job'
            formData={formData}
          />

          <FieldGrid>
            <FormField
              label='Enabled'
              fieldId='enabled'
              type='checkbox'
              formData={formData}
            />
            <FormField
              label='Schedule Type'
              fieldId='scheduleType'
              type='select'
              width='100%'
              options={scheduleTypeOptions}
              formData={formData}
            />
          </FieldGrid>

          {values.scheduleType === 'interval' ? (
            <FieldGrid>
              <FormField
                label='Every'
                fieldId='intervalValue'
                type='number'
                width='100%'
                formData={formData}
              />
              <FormField
                label='Unit'
                fieldId='intervalUnit'
                type='select'
                width='100%'
                options={unitOptions}
                formData={formData}
              />
            </FieldGrid>
          ) : (
            <>
              <FieldGrid>
                <FormField
                  label='Repeat'
                  fieldId='recurrence'
                  type='select'
                  width='100%'
                  options={recurrenceOptions}
                  formData={formData}
                />
                <FormField
                  label='Time'
                  fieldId='timeOfDay'
                  type='time'
                  width='100%'
                  formData={formData}
                />
              </FieldGrid>

              {values.recurrence === 'weekly' &&
                <FormField
                  label='Day of Week'
                  fieldId='dayOfWeek'
                  type='select'
                  width='100%'
                  options={dayOfWeekOptions}
                  formData={formData}
                />
              }

              {['monthly', 'yearly'].includes(values.recurrence) &&
                <FieldGrid>
                  {values.recurrence === 'yearly' &&
                    <FormField
                      label='Month'
                      fieldId='month'
                      type='select'
                      width='100%'
                      options={monthOptions}
                      formData={formData}
                    />
                  }
                  <FormField
                    label='Day of Month'
                    fieldId='dayOfMonth'
                    type='select'
                    width='100%'
                    options={numberOptions(1, 31)}
                    formData={formData}
                  />
                </FieldGrid>
              }
            </>
          )}

          <FormField
            label='Headers JSON'
            fieldId='headersText'
            type='textarea'
            width='100%'
            formData={formData}
          />

          <FormField
            label='Body'
            fieldId='body'
            type='textarea'
            width='100%'
            formData={formData}
          />

          <HiddenSubmit type='submit'>Submit</HiddenSubmit>
        </ModalContainer>
      </form>
    </Modal>,
    document.body
  )
}
